import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import { Layout } from '../components/Layout';
import { announcementAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { formatDate, formatDateTime } from '../lib/utils';
import {
  Megaphone,
  Plus,
  Loader2,
  Trash2,
  User,
  Clock,
} from 'lucide-react';

export default function Announcements() {
  const { user, canManage } = useAuth();
  const { selectedLocationId, isAllLocations } = useLocation();
  const activeLocId = isAllLocations ? user?.restaurant_id : selectedLocationId;
  const [loading, setLoading] = useState(true);
  const [announcements, setAnnouncements] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchAnnouncements();
  }, [user, activeLocId]);

  const fetchAnnouncements = async () => {
    if (!activeLocId) {
      setLoading(false);
      return;
    }

    try {
      const response = await announcementAPI.getAll(activeLocId);
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Error fetching announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await announcementAPI.create(activeLocId, formData);
      toast.success('Announcement posted!');
      setDialogOpen(false);
      setFormData({ title: '', message: '' });
      fetchAnnouncements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post announcement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (announcementId) => {
    if (!confirm('Delete this announcement?')) return;

    try {
      await announcementAPI.delete(announcementId);
      toast.success('Announcement deleted');
      fetchAnnouncements();
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Announcements
            </h1>
            <p className="text-slate-500 mt-1">
              Team updates and important notices
            </p>
          </div>

          {canManage && (
            <Button onClick={() => setDialogOpen(true)} data-testid="new-announcement-btn">
              <Plus className="mr-2 h-4 w-4" />
              New Announcement
            </Button>
          )}
        </div>

        {/* Announcements List */}
        {announcements.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="py-12">
              <div className="text-center">
                <Megaphone className="mx-auto h-12 w-12 text-slate-300" />
                <p className="mt-2 text-slate-500">No announcements yet</p>
                {canManage && (
                  <Button onClick={() => setDialogOpen(true)} className="mt-4">
                    <Plus className="mr-2 h-4 w-4" />
                    Post First Announcement
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {announcements.map((announcement) => (
              <Card key={announcement.id} className="border-slate-200" data-testid={`announcement-${announcement.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                          <Megaphone className="text-orange-600" size={20} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg text-slate-900">
                            {announcement.title}
                          </h3>
                          <div className="flex items-center gap-3 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <User size={14} />
                              {announcement.created_by_name}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock size={14} />
                              {formatDateTime(announcement.created_at)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <p className="text-slate-600 whitespace-pre-wrap pl-13">
                        {announcement.message}
                      </p>
                    </div>
                    {canManage && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-slate-400 hover:text-red-500"
                        onClick={() => handleDelete(announcement.id)}
                        data-testid={`delete-announcement-${announcement.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* New Announcement Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New Announcement</DialogTitle>
              <DialogDescription>
                Post an announcement to your team
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Announcement title"
                  required
                  data-testid="announcement-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Write your message here..."
                  rows={5}
                  required
                  data-testid="announcement-message-input"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => setDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-orange-500 hover:bg-orange-600"
                  disabled={submitting}
                  data-testid="announcement-submit-btn"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Post Announcement'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
